/**
 * 组件加载器
 * 实现约定式路由，将数据库中的组件路径自动映射到实际的Vue组件
 *
 * 约定规则：
 * - 数据库存储: system/user/index
 * - 实际路径: @/views/system/user/index.vue
 *
 * @author system
 * @version 1.0.0
 */

import { logError, ErrorType } from '@/utils/errorLogger'

/**
 * 图标映射表
 * 将后端图标名称映射到 Element Plus 图标组件名
 */
export const iconMap = {
  'system': 'Setting',
  'user': 'UserFilled',
  'peoples': 'User',
  'tree-table': 'Menu',
  'tree': 'OfficeBuilding',
  'build': 'OfficeBuilding',
  'list': 'Document',
  'component': 'Shop',
  'video': 'VideoPlay',
  'example': 'Promotion',
  'edit': 'EditPen',
  'people': 'User',
  'guide': 'Guide',
  'documentation': 'Document',
  'form': 'Form',
  'monitor': 'Monitor',
  'money': 'Money',
  'chart': 'TrendCharts',
  'dashboard': 'DataBoard',
  'grid': 'Grid',
  'key': 'Key',
  'Tickets': 'Tickets',
  'List': 'List',
  'Finished': 'Finished',
  'User': 'User',
  'Setting': 'Setting',
  'House': 'House',
  'Box': 'Box',
  'Coordinate': 'Coordinate',
  'Bell': 'Bell',
  'Star': 'Star',
  'Avatar': 'Avatar',
  'Calendar': 'Calendar',
  'ChatDotRound': 'ChatDotRound',
  'Notebook': 'Notebook',
  'Files': 'Files',
  'Management': 'Management',
  'Ticket': 'Ticket',
  'Cpu': 'Cpu',
  'Lightning': 'Lightning',
  'DataAnalysis': 'DataAnalysis',
  'DataBoard': 'DataBoard',
  'Shop': 'Shop',
  'Promotion': 'Promotion',
  'Present': 'Present',
  'Histogram': 'Histogram',
  'TrendCharts': 'TrendCharts',
  'Wallet': 'Wallet'
}

/**
 * 获取映射后的图标名称
 *
 * @param {string} icon - 原始图标名称
 * @returns {string} 映射后的图标名称
 */
export function getIcon(icon) {
  if (!icon) return 'Menu'
  return iconMap[icon] || icon
}

/**
 * 组件加载器
 * 根据约定式规则加载Vue组件
 *
 * @param {string} componentPath - 组件路径（如: system/user/index）
 * @returns {Function|Object} Vue组件或组件加载函数
 *
 * @example
 * // 普通组件
 * componentLoader('system/user/index')
 * // => () => import('@/views/system/user/index.vue')
 *
 * @example
 * // 布局组件
 * componentLoader('Layout')
 * // => () => import('@/layouts/index.vue')
 *
 * @example
 * // 空组件（目录占位）
 * componentLoader('Empty')
 * // => () => import('@/views/EmptyView.vue')
 */
export function componentLoader(componentPath) {
  // 1. 特殊组件：Layout（布局组件）
  if (componentPath === 'Layout') {
    return () => import('@/layouts/index.vue')
  }

  // 2. 特殊组件：Empty 或空值（目录占位组件）
  if (!componentPath || componentPath === 'Empty' || componentPath === 'null') {
    return () => import('@/views/EmptyView.vue')
  }

  // 3. 清理路径：移除开头的斜杠和.vue后缀
  let cleanPath = componentPath
  if (cleanPath.startsWith('/')) {
    cleanPath = cleanPath.slice(1)
  }
  if (cleanPath.endsWith('.vue')) {
    cleanPath = cleanPath.slice(0, -4)
  }

  // 4. 约定式路由：动态加载组件
  // 使用 Vite 的动态 import 语法
  return () => {
    // 构建完整路径
    const fullPath = `/src/views/${cleanPath}.vue`

    // 使用动态 import 加载组件
    // 注意：Vite 需要使用特定的 glob 模式来实现动态导入
    return loadComponent(cleanPath)
  }
}

/**
 * 动态加载组件
 * 使用 Vite 的 import.meta.glob 实现真正的动态加载
 *
 * @param {string} path - 组件相对路径
 * @returns {Promise<Component>} Vue组件
 */
async function loadComponent(path) {
  // 使用 Vite 的 glob 导入获取所有视图组件
  const modules = import.meta.glob('/src/views/**/*.vue')

  // 构建完整的模块路径
  const modulePath = `/src/views/${path}.vue`

  // 检查模块是否存在
  if (modules[modulePath]) {
    try {
      const module = await modules[modulePath]()
      return module.default || module
    } catch (error) {
      console.error(`[componentLoader] 组件加载失败: ${modulePath}`, error)

      const errorMsg = error.message || '组件加载失败'

      // 如果是模块加载失败（通常是 Vite 依赖缓存失效），尝试刷新页面一次
      if (isModuleLoadError(error) && !sessionStorage.getItem('_componentReloadAttempted')) {
        console.warn('[componentLoader] 检测到模块加载失败，可能是 Vite 缓存失效，尝试刷新页面...')
        sessionStorage.setItem('_componentReloadAttempted', '1')
        window.location.reload()
        return // reload 会中断执行
      }
      sessionStorage.removeItem('_componentReloadAttempted')

      // 记录错误日志（用 route path 作为 key，方便 ComponentLoadError 查找）
      logError({
        type: ErrorType.COMPONENT_LOAD_ERROR,
        path: window.location.pathname,
        modulePath: modulePath,
        message: errorMsg,
        stack: error.stack
      })

      // 降级到错误组件
      return loadErrorComponent(path, errorMsg)
    }
  } else {
    // 组件不存在
    const errorMessage = `组件文件不存在: ${modulePath}`
    console.warn(`[componentLoader] ${errorMessage}`)

    // 记录错误日志
    logError({
      type: ErrorType.COMPONENT_LOAD_ERROR,
      path: window.location.pathname,
      modulePath: modulePath,
      message: errorMessage
    })

    // 降级到错误组件
    return loadErrorComponent(path, errorMessage)
  }
}

/**
 * 判断是否为模块加载失败（Vite 依赖缓存失效等场景）
 */
function isModuleLoadError(error) {
  const msg = (error.message || '').toLowerCase()
  return msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('error loading module') ||
    msg.includes('unable to resolve') ||
    msg.includes('net::err_')
}

/**
 * 加载错误降级组件
 *
 * @param {string} path - 原始组件路径
 * @param {string} errorMessage - 错误消息
 * @returns {Promise<Component>} 错误组件
 */
async function loadErrorComponent(path, errorMessage) {
  try {
    // 尝试加载自定义的错误组件
    const errorModule = await import('@/views/error/ComponentLoadError.vue')
    return errorModule.default || errorModule
  } catch (e) {
    // 如果错误组件也不存在，返回一个简单的内联组件
    return {
      name: 'ComponentLoadError',
      props: {
        componentPath: {
          type: String,
          default: path
        },
        errorMessage: {
          type: String,
          default: errorMessage
        }
      },
      template: `
        <div style="padding: 40px; text-align: center; background: #fef0f0; border-radius: 8px; margin: 20px;">
          <h3 style="color: #f56c6c; margin-bottom: 12px;">组件加载失败</h3>
          <p style="color: #909399; margin-bottom: 16px;">{{ errorMessage || '该页面暂时无法访问' }}</p>
          <p style="color: #c0c4cc; font-size: 12px;">组件路径: {{ componentPath }}</p>
          <button
            @click="$router.push('/')"
            style="margin-top: 16px; padding: 8px 16px; background: #409eff; color: white; border: none; border-radius: 4px; cursor: pointer;"
          >
            返回首页
          </button>
        </div>
      `,
      data() {
        return {
          componentPath: path,
          errorMessage: errorMessage
        }
      }
    }
  }
}

export default componentLoader
